import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Target, Handshake, ChartBar, Clock, Calendar, MapPin, Users, Award, CheckCircle2 } from 'lucide-react';

const FORMATION_IMAGE = "https://images.unsplash.com/photo-1737574821698-862e77f044c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHx5b3VuZyUyMHByb2Zlc3Npb25hbCUyMGNvcnBvcmF0ZSUyMG9mZmljZXxlbnwwfHx8fDE3NzU3NDM5OTJ8MA&ixlib=rb-4.1.0&q=85";

const competenceBlocks = [
  {
    number: "01",
    title: "Prospecter & développer un portefeuille clients",
    hours: "168h",
    icon: Target,
    themes: ["Ciblage", "CRM", "LinkedIn", "Appels"],
    objective: "Trouver des prospects, les qualifier et décrocher des rendez-vous."
  },
  {
    number: "02",
    title: "Négocier & vendre une solution technique",
    hours: "196h",
    icon: Handshake,
    themes: ["Offres", "Négociation", "Closing"],
    objective: "Construire des offres et négocier face à des acheteurs professionnels."
  },
  {
    number: "03",
    title: "Suivre, fidéliser & gérer son activité",
    hours: "126h",
    icon: ChartBar,
    themes: ["KPI", "Reporting", "Fidélisation"],
    objective: "Piloter son portefeuille, fidéliser ses clients et rendre compte."
  }
];

const weekSchedule = [
  { day: "Lundi", location: "CFA", color: "bg-[#0B1F3A]" },
  { day: "Mardi", location: "Entreprise", color: "bg-[#C8A75A]" },
  { day: "Mercredi", location: "Entreprise", color: "bg-[#C8A75A]" },
  { day: "Jeudi", location: "Entreprise", color: "bg-[#C8A75A]" },
  { day: "Vendredi", location: "Entreprise", color: "bg-[#C8A75A]" },
];

export const Formation = () => {
  return (
    <div data-testid="formation-page">
      {/* Hero Section */}
      <section className="bg-[#0B1F3A] py-24" data-testid="formation-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Notre Formation
              </span>
              <h1 className="font-serif-heading text-4xl sm:text-5xl font-semibold text-white mb-6">
                TP Négociateur Technico-Commercial
              </h1>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              <p className="text-[#F5F5F5]/90 text-lg mb-8 leading-relaxed">
                Devenez un expert commercial en vente B2B, formé sur le terrain. 
                Une formation en alternance de 12 mois pour maîtriser toutes les techniques 
                de prospection, négociation et fidélisation.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">Niveau 5</span> • Équivalent BTS
                </div>
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">RNCP39063</span>
                </div>
                <div className="bg-white/10 px-4 py-2 text-white text-sm">
                  <span className="text-[#C8A75A] font-semibold">12 mois</span> • 490h centre
                </div>
              </div>

              <Link to="/etudiants#candidature">
                <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6" data-testid="formation-hero-cta">
                  Candidater maintenant
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img 
                src={FORMATION_IMAGE}
                alt="Négociateur Technico-Commercial"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Program Blocks */}
      <section className="py-24 bg-white" data-testid="program-blocks">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Programme
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A] mb-4">
              3 Blocs de Compétences
            </h2>
            <p className="text-[#4A4A4A] max-w-2xl mx-auto">
              Un parcours complet de 490 heures pour maîtriser tous les aspects 
              du métier de négociateur technico-commercial.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {competenceBlocks.map((block, index) => (
              <div 
                key={index}
                className="border border-[#E6E6E6] hover:border-[#C8A75A] transition-all hover-lift group"
                data-testid={`competence-block-${index}`}
              >
                {/* Header */}
                <div className="bg-[#0B1F3A] p-6 flex items-center justify-between">
                  <span className="text-[#C8A75A] font-serif-heading text-3xl font-semibold">
                    {block.number}
                  </span>
                  <div className="bg-[#C8A75A] px-3 py-1 text-[#0B1F3A] text-sm font-semibold">
                    {block.hours}
                  </div>
                </div>
                
                {/* Content */}
                <div className="p-6">
                  <block.icon className="text-[#C8A75A] mb-4" size={32} strokeWidth={1.5} />
                  <h3 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] mb-4">
                    {block.title}
                  </h3>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {block.themes.map((theme, i) => (
                      <span 
                        key={i}
                        className="bg-[#F5F5F5] text-[#4A4A4A] px-3 py-1 text-xs uppercase tracking-wide"
                      >
                        {theme}
                      </span>
                    ))}
                  </div>
                  
                  <p className="text-[#4A4A4A] text-sm leading-relaxed">
                    {block.objective}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/programme">
              <Button className="bg-[#0B1F3A] text-white hover:bg-[#061326] rounded-none px-8 py-4" data-testid="view-full-program">
                Voir le programme détaillé
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Rhythm Section */}
      <section className="py-24 bg-[#F5F5F5]" data-testid="rhythm-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
                Rythme d'alternance
              </span>
              <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A] mb-6">
                1 jour CFA • 4 jours Entreprise
              </h2>
              <div className="w-16 h-0.5 bg-[#C8A75A] mb-6"></div>
              <p className="text-[#4A4A4A] mb-8 leading-relaxed">
                Le rythme le plus productif du marché. Vous êtes immergé dans votre métier 
                dès la première semaine, avec un encadrement pédagogique de proximité.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#0B1F3A] flex items-center justify-center">
                    <Clock className="text-[#C8A75A]" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B1F3A]">100% Présentiel</div>
                    <div className="text-sm text-[#4A4A4A]">Aucun cours en distanciel</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#0B1F3A] flex items-center justify-center">
                    <MapPin className="text-[#C8A75A]" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B1F3A]">Campus Saint-Denis (93)</div>
                    <div className="text-sm text-[#4A4A4A]">Accessible métro ligne 13</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#0B1F3A] flex items-center justify-center">
                    <Users className="text-[#C8A75A]" size={24} />
                  </div>
                  <div>
                    <div className="font-semibold text-[#0B1F3A]">20 places par promotion</div>
                    <div className="text-sm text-[#4A4A4A]">Encadrement personnalisé</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Week Schedule */}
            <div className="bg-white p-8 border border-[#E6E6E6]">
              <h3 className="font-serif-heading text-xl font-semibold text-[#0B1F3A] mb-6 text-center">
                Semaine Type
              </h3>
              <div className="space-y-3">
                {weekSchedule.map((day, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-4 ${day.color} text-white`}
                  >
                    <span className="font-medium">{day.day}</span>
                    <span className="text-sm opacity-90">{day.location}</span>
                  </div>
                ))}
              </div>
              <div className="mt-6 pt-6 border-t border-[#E6E6E6]">
                <div className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[#0B1F3A]"></div>
                    <span>CFA - 20%</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-[#C8A75A]"></div>
                    <span>Entreprise - 80%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diploma & Certification */}
      <section className="py-24 bg-[#0B1F3A]" data-testid="diploma-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Certification
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-white mb-4">
              Un diplôme reconnu par l'État
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur border border-white/10 p-6 text-center">
              <Award className="mx-auto text-[#C8A75A] mb-4" size={40} strokeWidth={1.5} />
              <h3 className="text-white font-semibold mb-2">Titre RNCP</h3>
              <p className="text-[#F5F5F5]/70 text-sm">RNCP39063 Niveau 5</p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 p-6 text-center">
              <CheckCircle2 className="mx-auto text-[#C8A75A] mb-4" size={40} strokeWidth={1.5} />
              <h3 className="text-white font-semibold mb-2">Équivalent BTS</h3>
              <p className="text-[#F5F5F5]/70 text-sm">Bac+2 reconnu</p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 p-6 text-center">
              <Target className="mx-auto text-[#C8A75A] mb-4" size={40} strokeWidth={1.5} />
              <h3 className="text-white font-semibold mb-2">Validable par bloc</h3>
              <p className="text-[#F5F5F5]/70 text-sm">Progression flexible</p>
            </div>
            <div className="bg-white/5 backdrop-blur border border-white/10 p-6 text-center">
              <ChartBar className="mx-auto text-[#C8A75A] mb-4" size={40} strokeWidth={1.5} />
              <h3 className="text-white font-semibold mb-2">Passerelle Bachelor</h3>
              <p className="text-[#F5F5F5]/70 text-sm">Accès Bac+3 (Sept 2027)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enrollment Dates */}
      <section className="py-24 bg-white" data-testid="enrollment-dates">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#C8A75A] mb-4 block">
              Inscriptions
            </span>
            <h2 className="font-serif-heading text-3xl sm:text-4xl font-semibold text-[#0B1F3A]">
              3 rentrées par an
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { month: "Septembre", year: "2026" },
              { month: "Janvier", year: "2027" },
              { month: "Avril", year: "2027" }
            ].map((date, index) => (
              <div 
                key={index}
                className="border-2 border-[#0B1F3A] p-8 text-center hover:bg-[#0B1F3A] hover:text-white transition-colors group"
                data-testid={`enrollment-date-${index}`}
              >
                <Calendar className="mx-auto text-[#C8A75A] mb-4" size={32} />
                <div className="font-serif-heading text-2xl font-semibold group-hover:text-white text-[#0B1F3A]">
                  {date.month}
                </div>
                <div className="text-[#4A4A4A] group-hover:text-[#F5F5F5]/70">
                  {date.year}
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/etudiants#candidature">
              <Button className="bg-[#C8A75A] text-[#0B1F3A] hover:bg-[#DDBF78] rounded-none px-8 py-6 text-base font-semibold" data-testid="enrollment-cta">
                Déposer ma candidature
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <Link to="/planning">
              <Button variant="outline" className="border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-none px-8 py-6 text-base" data-testid="planning-cta">
                Voir le planning complet
                <Calendar className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Formation;
